import type React from 'react';
import { Component, useEffect, useRef } from 'react';
import type { RefObject } from 'react';

import { useFocusRing } from '@react-aria/focus';
import { useSwitch } from '@react-aria/switch';
import { useUpdateEffect } from '@react-aria/utils';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { observer } from 'mobx-react';

import { useToggleState } from '@react-stately/toggle';
import type { ToggleState } from '@react-stately/toggle';

import type { AriaSwitchProps } from '@react-types/switch';

export interface ISwitchProps extends AriaSwitchProps {
  disabled?: boolean;
  label?: React.ReactNode;
  testId?: string;
}

const bgBlue = '#40ADFF';
const bgGray = '#CBD5E1';
const bgWhite = '#ffffff';

const SwitchAria = (props: ISwitchProps) => {
  const { value, defaultSelected } = props;
  let state: ToggleState = useToggleState(props);
  let ref: RefObject<HTMLInputElement> = useRef(null);
  let { inputProps } = useSwitch(props, state, ref);
  let { isFocusVisible, focusProps } = useFocusRing();
  const { isSelected } = state;

  const { children, disabled, testId } = props;

  useEffect(() => {
    if (value || defaultSelected) {
      state.setSelected(true);
    }
  }, [value]);

  useUpdateEffect(() => {
    state.setSelected(value);
  }, [value]);

  return (
    <label className="flex items-center">
      <VisuallyHidden>
        <input {...inputProps} {...focusProps} ref={ref} disabled={disabled} data-testid={testId} />
      </VisuallyHidden>
      <svg
        className={`h-8 w-14 max-w-md ${children && 'mr-2'} ${!disabled && 'cursor-pointer'}`}
        aria-hidden="true"
      >
        <rect
          x={3}
          y={3}
          width={44}
          height={24}
          rx={12}
          fill={isSelected && !disabled ? bgBlue : !disabled ? bgGray : bgGray}
          stroke={isSelected && !disabled ? bgBlue : bgGray}
        />
        <circle
          cx={isSelected ? 35 : 15}
          cy={15}
          r={10}
          fill={isSelected ? bgWhite : !isSelected && disabled ? bgWhite : bgWhite}
        />
        {isFocusVisible && (
          <rect
            x={3}
            y={3}
            width={44}
            height={24}
            rx={12}
            fill="none"
            stroke={bgGray}
            strokeWidth={2}
          />
        )}
      </svg>
      {children}
    </label>
  );
};

@observer
class ClickSwitch extends Component<ISwitchProps> {
  render() {
    const { label } = this.props;
    const toggle = <SwitchAria {...this.props} aria-label="switch-aria" />;

    if (label) {
      return (
        <label className="mb-5 flex cursor-pointer items-center justify-between space-x-2 last:mb-0">
          <div className="text-base font-medium">{label}</div>
          <div className="relative">{toggle}</div>
        </label>
      );
    }

    return toggle;
  }
}

export default ClickSwitch;
